import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CircleDashed, Loader2, Minus, X } from 'lucide-react'
import { DEFAULT_SERVICES, TEMPLATE_ADDONS, type ProjectSetup, type ProjectFromFile, type ProjectSetupCreate, type ProjectSetupList, type ProjectServicesConfig, type SetupOptions, type SetupStep, type SetupStepName, type TemplateAddon } from '@one-deployment/api'
import { Button as SButton } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Button, Card, Label, Select, SkeletonRows, Textarea } from '@/components/ui'
import { ErrorState } from '@/components/status'
import { del, enc, get, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { ServicesForm } from './Services'

const ADDON_LABEL: Record<TemplateAddon, string> = { stripe: 'Stripe payments', orgs: 'Teams / organisations', analytics: 'Analytics (PostHog)', i18n: 'Multiple languages' }
const STEP_LABEL: Record<SetupStepName, string> = {
  repo: 'GitHub repo from main-template',
  branches: 'Branches dev, uat, prod',
  project: 'Project on the dashboard',
  secrets: 'Secrets from the pasted .env',
  apply: 'Project file in the repo, applied',
  supabase: 'Supabase dev, uat, prod',
  cloudflare: 'Cloudflare Pages + GitHub Environments',
  trigger: 'Trigger.dev project',
  expo: 'Expo project',
  links: 'Link Linear project and Slack channel',
  hosts: 'Hosts and secrets (Vercel, Cloudflare)',
  container: 'Container',
  session: 'Main Claude session (runs the generator, opens a PR into dev)',
}
const setupKey = (id: string) => ['project-setup', id] as const

/**
 * Create project from main-template (spec 06): services, add-ons, and the
 * Linear project and Slack channel to use. Both are made by hand beforehand;
 * setup only picks them.
 */
export function NewFromTemplate({ name, projectKey, onBack, onClose }: { name: string; projectKey: string; onBack: () => void; onClose: () => void }) {
  const [step, setStep] = useState(0)
  const [services, setServices] = useState<ProjectServicesConfig>({ ...DEFAULT_SERVICES, jobs: 'cloudflare' })
  const [addons, setAddons] = useState<TemplateAddon[]>([])
  const [linearProject, setLinearProject] = useState('')
  const [slack, setSlack] = useState('')
  const [setupId, setSetupId] = useState<string | null>(null)
  const qc = useQueryClient()
  const opts = useQuery({ queryKey: ['project-setup', 'options'], queryFn: ({ signal }) => get<SetupOptions>('/api/project-setups/options', signal), enabled: step === 2 })
  const register = useMutation({ mutationFn: () => post('/api/project-setups/template'), onSuccess: () => void opts.refetch() })
  const start = useMutation({
    mutationFn: () => {
      const lp = opts.data?.linearProjects.find((p) => p.id === linearProject)
      const body: ProjectSetupCreate = {
        key: projectKey, name, services, addons,
        ...(lp ? { linearProjectId: lp.id, linearTeamId: lp.teamIds[0] } : {}),
        ...(slack ? { slackChannel: slack } : {}),
      }
      return post<ProjectSetup>('/api/project-setups', body)
    },
    onSuccess: (r) => { qc.setQueryData(setupKey(r.id), r); void qc.invalidateQueries({ queryKey: ['project-setups'] }); setSetupId(r.id) },
  })

  if (setupId) return (
    <>
      <SetupChecklist id={setupId} />
      <div className="mt-4 flex justify-end"><Button onClick={onClose}>Close (it keeps going)</Button></div>
    </>
  )

  return (
    <>
      <div className="min-h-48 space-y-3">
        {step === 0 && <ServicesForm value={services} onChange={(v) => setServices({ ...v, frontend: 'cloudflare', backend: 'cloudflare' })} />}
        {step === 0 && <p className="text-xs text-fg-subtle">New projects run on Cloudflare; Vercel is only for existing projects.</p>}
        {step === 1 && <>
          <p className="text-xs text-fg-subtle">Ticked add-ons stay in the code; the rest are removed by the generator.</p>
          {TEMPLATE_ADDONS.map((a) => (
            <label key={a} className="flex h-7 items-center gap-2 text-sm">
              <Checkbox checked={addons.includes(a)} onCheckedChange={(v) => setAddons(v === true ? [...addons, a] : addons.filter((x) => x !== a))} />{ADDON_LABEL[a]}
            </label>
          ))}
        </>}
        {step === 2 && (opts.error ? <ErrorState error={opts.error} compact retry={() => void opts.refetch()} /> : !opts.data ? <SkeletonRows rows={3} /> : <>
          <div><Label htmlFor="ns-linear">Linear project (make it in Linear first)</Label>
            <Select id="ns-linear" className="w-full" value={linearProject} onChange={(e) => setLinearProject(e.target.value)}>
              <option value="">None for now</option>
              {opts.data.linearProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select></div>
          <div><Label htmlFor="ns-slack">Slack channel (make it in Slack first and invite the bot)</Label>
            <Select id="ns-slack" className="w-full" value={slack} onChange={(e) => setSlack(e.target.value)}>
              <option value="">None for now</option>
              {opts.data.slackChannels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </Select></div>
          {!opts.data.templateRegistered && <p className="text-xs text-fg-subtle">main-template is not on the dashboard yet. <SButton variant="link" className="h-auto p-0 text-xs font-normal text-accent" disabled={register.isPending} onClick={() => register.mutate()}>Add it as an internal project</SButton>{register.error && <span className="text-err"> {register.error.message}</span>}</p>}
          {opts.data.notes.length > 0 && <p className="text-xs text-warn">{opts.data.notes.join('. ')}.</p>}
          <p className="text-xs text-fg-subtle">Setup makes: repo <span className="font-mono">TriSof-Ltd/{projectKey}</span>, branches dev/uat/prod, Supabase <span className="font-mono">{projectKey}-dev/-uat/-prod</span>, Cloudflare at <span className="font-mono">uat.{projectKey}.onedeployment.co</span>{services.mobile && ', an Expo project'}, a container and the main session. A failed step stops and waits for Retry; nothing is deleted.</p>
        </>)}
      </div>
      {start.error && <div className="mt-3"><ErrorState error={start.error} compact /></div>}
      <div className="mt-4 flex justify-between">
        <Button onClick={() => (step === 0 ? onBack() : setStep(step - 1))}>Back</Button>
        {step < 2 ? <Button variant="primary" onClick={() => setStep(step + 1)}>Next</Button>
          : <Button variant="primary" disabled={start.isPending || !opts.data} onClick={() => start.mutate()}>Start setup</Button>}
      </div>
    </>
  )
}

/**
 * Recreate a project from its `.trisof/project.yml` and a .env of its secret
 * values (features/23). Values are stored encrypted; Settings → Secrets shows them.
 */
export function NewFromFile({ onBack, onClose }: { onBack: () => void; onClose: () => void }) {
  const [file, setFile] = useState('')
  const [secrets, setSecrets] = useState('')
  const [setupId, setSetupId] = useState<string | null>(null)
  const qc = useQueryClient()
  const start = useMutation({
    mutationFn: () => post<ProjectSetup>('/api/project-setups/from-file', { file, ...(secrets.trim() ? { secrets } : {}) } satisfies ProjectFromFile),
    onSuccess: (r) => { setSecrets(''); qc.setQueryData(setupKey(r.id), r); void qc.invalidateQueries({ queryKey: ['project-setups'] }); setSetupId(r.id) },
  })
  if (setupId) return (
    <>
      <SetupChecklist id={setupId} />
      <div className="mt-4 flex justify-end"><Button onClick={onClose}>Close (it keeps going)</Button></div>
    </>
  )
  return (
    <>
      <div className="space-y-3">
        <div><Label htmlFor="nf-file">project.yml</Label>
          <Textarea id="nf-file" rows={10} className="w-full font-mono text-xs" value={file} onChange={(e) => setFile(e.target.value)} placeholder="version: 1&#10;project: …" /></div>
        <div><Label htmlFor="nf-secrets">Secret values (.env lines, optional; add them later in Settings → Secrets)</Label>
          <Textarea id="nf-secrets" rows={5} className="w-full font-mono text-xs" autoComplete="off" spellCheck={false} value={secrets} onChange={(e) => setSecrets(e.target.value)} placeholder="NAME=value" /></div>
        <p className="text-xs text-fg-subtle">Uses the repo the file names (made from its template if missing), the accounts on Connections by label, and the Linear team and project it names. A failed step waits for Retry; nothing is deleted.</p>
      </div>
      {start.error && <div className="mt-3"><ErrorState error={start.error} compact /></div>}
      <div className="mt-4 flex justify-between">
        <Button onClick={onBack}>Back</Button>
        <Button variant="primary" disabled={start.isPending || !file.trim()} onClick={() => start.mutate()}>Start setup</Button>
      </div>
    </>
  )
}

function StepIcon({ s }: { s: SetupStep['state'] }) {
  if (s === 'done') return <Check size={14} className="text-ok" />
  if (s === 'running') return <Loader2 size={14} className="animate-spin text-accent" />
  if (s === 'failed') return <X size={14} className="text-err" />
  if (s === 'skipped') return <Minus size={14} className="text-fg-faint" />
  return <CircleDashed size={14} className="text-fg-faint" />
}

/** The setup's steps, live while it runs, with Retry on a failed one. */
export function SetupChecklist({ id }: { id: string }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: setupKey(id),
    queryFn: ({ signal }) => get<ProjectSetup>(`/api/project-setups/${enc(id)}`, signal),
    refetchInterval: (query) => (query.state.data?.state === 'running' ? 3000 : false),
  })
  const act = useMutation({
    mutationFn: ({ step, what }: { step: SetupStepName; what: 'retry' | 'done' }) => post<ProjectSetup>(`/api/project-setups/${enc(id)}/steps/${step}/${what}`),
    onSuccess: (r) => qc.setQueryData(setupKey(id), r),
  })
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />
  if (!q.data) return <SkeletonRows rows={10} />
  const s = q.data
  return (
    <div className="space-y-1.5">
      {s.steps.map((st) => (
        <div key={st.step} className={cn('rounded-md px-2 py-1.5', st.state === 'failed' && 'bg-err/10')}>
          <div className="flex items-center gap-2 text-sm">
            <StepIcon s={st.state} />
            <span className={cn('flex-1', st.state === 'skipped' && 'text-fg-faint')}>{STEP_LABEL[st.step]}</span>
            {st.state === 'failed' && <>
              {st.step === 'trigger' && <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ step: st.step, what: 'done' })}>Mark done</Button>}
              <Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate({ step: st.step, what: 'retry' })}>Retry</Button>
            </>}
          </div>
          {st.error && <p className="mt-1 ml-6 text-xs text-err">{st.error}</p>}
          {Array.isArray(st.output?.notes) && (st.output.notes as string[]).map((n) => <p key={n} className="mt-1 ml-6 text-xs text-warn">{n}</p>)}
        </div>
      ))}
      {act.error && <ErrorState error={act.error} compact />}
      {s.projectKey && <p className="pt-2 text-xs"><Link to="/p/$project" params={{ project: s.projectKey }} className="text-accent hover:underline">Open {s.name}</Link></p>}
    </div>
  )
}

/** Setups not finished yet, on Home, as cards like the project cards (red while a step has failed), so a closed dialog is not the only way back. */
export function SetupsInProgress() {
  const q = useQuery({ queryKey: ['project-setups'], queryFn: ({ signal }) => get<ProjectSetupList>('/api/project-setups', signal), refetchInterval: 15_000 })
  const open = (q.data?.setups ?? []).filter((s) => s.state !== 'done')
  return <>{open.map((s) => <SetupCard key={s.id} s={s} />)}</>
}

function SetupCard({ s }: { s: ProjectSetup }) {
  const qc = useQueryClient()
  const act = useMutation({
    mutationFn: (step: SetupStepName) => post<ProjectSetup>(`/api/project-setups/${enc(s.id)}/steps/${step}/retry`),
    onSuccess: (r) => { qc.setQueryData(setupKey(s.id), r); void qc.invalidateQueries({ queryKey: ['project-setups'] }) },
  })
  const remove = useMutation({
    mutationFn: () => del(`/api/project-setups/${enc(s.id)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['project-setups'] }),
  })
  const failed = s.steps.find((st) => st.state === 'failed')
  const current = failed ?? s.steps.find((st) => st.state === 'running') ?? s.steps.find((st) => st.state === 'pending')
  const done = s.steps.filter((st) => st.state === 'done' || st.state === 'skipped').length
  // Drawn like a project card: the name, a red dot when a step failed (hover for why, and Retry), then progress.
  return (
    <div className="flex h-full flex-col gap-3 rounded-lg border border-border bg-surface-2 p-4">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="mr-0.5 truncate font-semibold">{s.name}</span>
        {failed && (
          <HoverCard openDelay={100} closeDelay={150}>
            <HoverCardTrigger asChild>
              <button type="button" aria-label="Setup failed" className="flex h-5 shrink-0 items-center rounded-full bg-err/15 px-1.5"><span className="size-1.5 rounded-full bg-err" /></button>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-80 space-y-2 p-3 text-xs">
              <div className="font-medium text-fg">{STEP_LABEL[failed.step]} failed</div>
              <p className="text-err">{failed.error}</p>
              <div className="flex justify-end gap-2"><Button size="sm" disabled={remove.isPending} onClick={() => remove.mutate()}>{remove.isPending ? 'Removing…' : 'Remove'}</Button><Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate(failed.step)}>{act.isPending ? 'Retrying…' : 'Retry'}</Button></div>
            </HoverCardContent>
          </HoverCard>
        )}
      </div>
      <div className="text-xs text-fg-faint">Setting up · {done}/{s.steps.length}{current && !failed ? ` · ${STEP_LABEL[current.step]}` : ''}</div>
    </div>
  )
}
